import type { ComponentCommonDays, KeyDatePage } from '@edwin-group/people-graphql';
import { Menu, Transition, Portal } from '@headlessui/react';
import { Fragment, useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { trackGAEvent } from '@/lib/ga-custom-events/dispatcher';
import { downloadCalendarEvent, generateGoogleCalendarUrl, generateMicrosoftUrl } from '../../utils/calendar-utils';

interface CalendarDropdownProps {
  event: ComponentCommonDays;
  page?: KeyDatePage | null;
}

export const CalendarDropdown = ({ event, page }: CalendarDropdownProps) => {
  const calendarOptions = useMemo(
    () => [
      {
        name: page?.calendarAppleLabel ?? 'Apple',
        action: 'download',
      },
      {
        name: page?.calendarGoogleLabel ?? 'Google',
        action: 'google',
      },
      {
        name: page?.calendarOutlookLabel ?? 'Outlook',
        action: 'outlook',
      },
    ],
    [page],
  );
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  const updatePosition = useCallback(() => {
    if (buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setPosition({
        top: rect.bottom + window.scrollY + 8,
        left: rect.left + window.scrollX,
      });
    }
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [updatePosition]);

  const handleCalendarAction = (action: string) => {
    trackGAEvent('added_to_calendar', {
      event_name: event?.name ?? '',
    });

    if (action === 'download') {
      downloadCalendarEvent(event);
    } else {
      const urlMap = {
        google: generateGoogleCalendarUrl,
        outlook: generateMicrosoftUrl,
      };

      const urlGenerator = urlMap[action as keyof typeof urlMap];
      if (urlGenerator) {
        window.open(urlGenerator(event), '_blank');
      }
    }
  };

  return (
    <Menu as="div" className="relative inline-block text-left">
      <Menu.Button
        ref={buttonRef}
        onClick={updatePosition}
        className="!my-0 !py-0 font-haffer text-sh-xxxs font-bold !text-people-teal-600 hover:opacity-50">
        {page?.addToCalendarLabel}
      </Menu.Button>
      <Portal>
        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95">
          <Menu.Items
            style={{ position: 'absolute', top: position.top, left: position.left }}
            className="z-50 w-56 origin-top-left rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
            <div className="py-1">
              {calendarOptions.map((option) => (
                <Menu.Item key={option.name}>
                  {({ active }) => (
                    <button
                      onClick={() => handleCalendarAction(option.action)}
                      className={`${
                        active ? 'bg-gray-50' : ''
                      } flex w-full items-center px-4 py-2 text-sh-xxs text-gray-900`}>
                      <span>{option.name}</span>
                    </button>
                  )}
                </Menu.Item>
              ))}
            </div>
          </Menu.Items>
        </Transition>
      </Portal>
    </Menu>
  );
};
